import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, useMediaQuery, Skeleton } from "@mui/material";
import { GpsFixed as GpsFixedIcon, LaunchOutlined } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { RootState } from "../../../core/store";
import {
  fetchActiveSessions,
  TrackingSession,
  TrackingPosition,
  TrackingState,
} from "../../../shared/tracking/trackingSlice";
import useTrackingHub from "../../../shared/tracking/hooks/useTrackingHub";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dtGreen } from "../../../shared/themes/redesignTokens";
import TrackingMapContainer, { TrackingStatus } from "./map/TrackingMapContainer";
import ConnectionBanner from "./ConnectionBanner";
import ConfettiParticles from "./ConfettiParticles";
import TrackingSkeleton from "./TrackingSkeleton";
import DesktopTrackingLayout from "./layouts/DesktopTrackingLayout";
import TabletTrackingLayout from "./layouts/TabletTrackingLayout";
import PhoneTrackingLayout from "./layouts/PhoneTrackingLayout";

// ===================================================================
//  THRESHOLDS
// ===================================================================

const STATIONARY_THRESHOLD_MS = 5 * 60 * 1000; // 5 min
const ARRIVAL_CELEBRATION_MS = 2000; // 2s confetti

type BottomSheetState = "peek" | "half" | "full";

// ===================================================================
//  MAIN COMPONENT
// ===================================================================

const ServiceTrackingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation(["serviceTracking", "servicing"]);

  const isDesktop = useMediaQuery("(min-width:1200px)");
  const isTablet = useMediaQuery("(min-width:768px) and (max-width:1199px)");
  const isPhone = useMediaQuery("(max-width:767px)");

  const { activeSessions, positions, loading } =
    useAppSelector<TrackingState>((state: RootState) => state.tracking);

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [bottomSheet, setBottomSheet] = useState<BottomSheetState>("peek");
  const [isCelebrating, setIsCelebrating] = useState(false);
  const [prevPosition, setPrevPosition] = useState<TrackingPosition | null>(null);
  const prevStatusRef = useRef<number | null>(null);
  const prevPositionRef = useRef<TrackingPosition | null>(null);

  // Fetch active sessions on mount
  useEffect(() => {
    dispatch(fetchActiveSessions());
  }, [dispatch]);

  // Auto-select first session
  useEffect(() => {
    if (activeSessions.length > 0 && !selectedSessionId) {
      setSelectedSessionId(activeSessions[0].id);
    }
  }, [activeSessions, selectedSessionId]);

  // Subscribe to real-time updates
  const { latestPosition, isConnected } = useTrackingHub(selectedSessionId);

  const selectedSession = useMemo(
    () => activeSessions.find((s) => s.id === selectedSessionId) || null,
    [activeSessions, selectedSessionId]
  );

  const currentPosition = useMemo(() => {
    if (latestPosition) return latestPosition;
    if (selectedSessionId && positions[selectedSessionId]) {
      return positions[selectedSessionId];
    }
    return null;
  }, [latestPosition, selectedSessionId, positions]);

  // Track previous position for heading calculation
  useEffect(() => {
    if (currentPosition) {
      setPrevPosition(prevPositionRef.current);
      prevPositionRef.current = currentPosition;
    }
  }, [currentPosition]);

  const isStationary = useMemo(() => {
    if (!currentPosition?.recordedAt) return false;
    const elapsed = Date.now() - new Date(currentPosition.recordedAt).getTime();
    return elapsed > STATIONARY_THRESHOLD_MS;
  }, [currentPosition?.recordedAt]);

  // Arrival celebration: detect status change to Arrived
  useEffect(() => {
    const currentStatus = selectedSession?.status ?? null;
    if (
      prevStatusRef.current != null &&
      prevStatusRef.current !== TrackingStatus.Arrived &&
      currentStatus === TrackingStatus.Arrived
    ) {
      setIsCelebrating(true);
      const timer = setTimeout(() => setIsCelebrating(false), ARRIVAL_CELEBRATION_MS);
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = currentStatus;
  }, [selectedSession?.status]);

  const handleSessionClick = useCallback(
    (session: TrackingSession) => {
      setSelectedSessionId(session.id);
      if (isPhone) setBottomSheet("half");
    },
    [isPhone]
  );

  const toggleBottomSheet = useCallback(() => {
    setBottomSheet((prev) =>
      prev === "peek" ? "half" : prev === "half" ? "full" : "peek"
    );
  }, []);

  // --- Skeleton loading ---
  if (loading && activeSessions.length === 0) {
    return <TrackingSkeleton isDesktop={isDesktop} isTablet={isTablet} isPhone={isPhone} />;
  }

  // --- No active services ---
  if (activeSessions.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 120px)",
          backgroundColor: dtGreen.pageBg,
          gap: 2,
        }}
      >
        <GpsFixedIcon sx={{ fontSize: 48, color: dtGreen.textMuted, opacity: 0.5 }} />
        <Box
          sx={{
            fontFamily: dtGreen.font,
            fontSize: "16px",
            fontWeight: 500,
            color: dtGreen.textSecondary,
          }}
        >
          {translate("serviceTracking:noActiveServices")}
        </Box>
      </Box>
    );
  }

  // Shared elements
  const mapElement = (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <TrackingMapContainer
        session={selectedSession}
        position={currentPosition}
        prevPosition={prevPosition}
        isStationary={isStationary}
        isArrivalCelebrating={isCelebrating}
      />
      <ConfettiParticles active={isCelebrating} />
    </Box>
  );

  const connectionBanner = (
    <Box>
      <ConnectionBanner
        lastUpdateTime={currentPosition?.recordedAt ?? null}
        isConnected={isConnected}
      />
      {selectedSession?.serviceRequestId && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 0.5 }}>
          <Button
            size="small"
            endIcon={<LaunchOutlined sx={{ fontSize: "14px !important" }} />}
            onClick={() => navigate(`/service-board/${selectedSession.serviceRequestId}`)}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              color: dtGreen.textSecondary,
            }}
          >
            {translate("servicing:crossLinks.viewServiceRequest")}
          </Button>
        </Box>
      )}
    </Box>
  );

  if (isPhone) {
    return (
      <PhoneTrackingLayout
        activeSessions={activeSessions}
        positions={positions}
        selectedSessionId={selectedSessionId}
        bottomSheet={bottomSheet}
        onSessionClick={handleSessionClick}
        onToggleBottomSheet={toggleBottomSheet}
        onSetBottomSheet={setBottomSheet}
        mapElement={mapElement}
        connectionBanner={connectionBanner}
      />
    );
  }

  if (isTablet) {
    return (
      <TabletTrackingLayout
        activeSessions={activeSessions}
        positions={positions}
        selectedSessionId={selectedSessionId}
        onSessionClick={handleSessionClick}
        mapElement={mapElement}
        connectionBanner={connectionBanner}
      />
    );
  }

  return (
    <DesktopTrackingLayout
      activeSessions={activeSessions}
      positions={positions}
      selectedSessionId={selectedSessionId}
      onSessionClick={handleSessionClick}
      mapElement={mapElement}
      connectionBanner={connectionBanner}
    />
  );
};

export default ServiceTrackingPage;
